import React, { createContext, useContext, useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { supabase, getMyRestaurant, setPortalToken } from './lib/supabase';

import LoginPage       from './pages/LoginPage';
import ForgotPassword  from './pages/ForgotPassword';
import ResetPassword   from './pages/ResetPassword';
import RegisterPage    from './pages/RegisterPage';
import Layout          from './components/Layout';
import Dashboard       from './pages/Dashboard';
import Orders          from './pages/Orders';
import Reservations    from './pages/Reservations';
import Menu            from './pages/Menu';
import Customers       from './pages/Customers';
import Settings        from './pages/Settings';
import Analytics       from './pages/Analytics';
import Reviews         from './pages/Reviews';
import Campaigns       from './pages/Campaigns';
import Settlements     from './pages/Settlements';
import Loyalty         from './pages/Loyalty';
import Payouts         from './pages/Payouts';
import Team           from './pages/Team';

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

function AuthProvider({ children }) {
  const [user, setUser]             = useState(null);
  const [restaurant, setRestaurant] = useState(null);
  const [loading, setLoading]       = useState(true);
  const loadingRef = React.useRef(false);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        const currentUser  = session?.user ?? null;
        const currentToken = session?.access_token ?? null;
        setUser(currentUser);
        if (currentUser) {
          await loadRestaurant(currentUser.id, currentToken);
        } else {
          setRestaurant(null);
          setLoading(false);
        }
      }
    );
    return () => subscription.unsubscribe();
  }, []);

  const loadRestaurant = async (userId, token = null) => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    setLoading(true);
    try {
      const r = await Promise.race([
        getMyRestaurant(userId, token),
        new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), 8000))
      ]);
      setRestaurant(r);
    } catch (err) {
      console.error('loadRestaurant error:', err.message);
      setRestaurant(null);
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setRestaurant(null);
  };

  return (
    <AuthContext.Provider value={{
      user, restaurant, loading, signOut,
      loadRestaurant: () => loadRestaurant(user?.id)
    }}>
      {children}
    </AuthContext.Provider>
  );
}

function ProtectedRoute({ children }) {
  const { user, restaurant, loading } = useAuth();

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: 'var(--cream)' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: 40, height: 40, border: '3px solid var(--border)', borderTopColor: 'var(--orange)', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 1rem' }}/>
          <p style={{ color: 'var(--muted)', fontSize: '0.9rem' }}>Loading your restaurant...</p>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;
  if (!restaurant) return <Navigate to="/no-restaurant" replace />;
  return children;
}

function NoRestaurantPage() {
  const { signOut, user } = useAuth();
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: 'var(--cream)', padding: '2rem' }}>
      <div style={{ background: 'white', borderRadius: 16, padding: '3rem 2.5rem', maxWidth: 440, width: '100%', textAlign: 'center', border: '1px solid var(--border)' }}>
        <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>🍽️</div>
        <h2 style={{ fontFamily: 'Fraunces, serif', fontSize: '1.5rem', marginBottom: '0.8rem' }}>Restaurant not found</h2>
        <p style={{ color: 'var(--mid)', fontSize: '0.9rem', lineHeight: 1.6, marginBottom: '1.5rem' }}>
          We couldn't find a restaurant linked to <strong>{user?.email}</strong>.
          Your restaurant may still be pending activation, or the email may not match our records.
        </p>
        <p style={{ color: 'var(--mid)', fontSize: '0.9rem', marginBottom: '2rem' }}>
          Contact us at{' '}
          <a href="mailto:support@tablechat-ai.com" style={{ color: 'var(--orange)' }}>support@tablechat-ai.com</a>{' '}
          and we'll get you set up.
        </p>
        <button onClick={signOut} style={{ background: 'var(--warm)', border: '1px solid var(--border)', color: 'var(--ink)', padding: '0.75rem 1.5rem', borderRadius: 8, fontWeight: 600, fontSize: '0.9rem' }}>
          Sign out
        </button>
      </div>
    </div>
  );
}

// Reviewers (role "reviews") only get the Reviews page. Owners get everything.
function PortalRoutes() {
  const { restaurant } = useAuth();
  if (restaurant?.role === 'reviews') {
    return (
      <Routes>
        <Route path="/reviews" element={<Reviews />} />
        <Route path="*"        element={<Navigate to="/reviews" replace />} />
      </Routes>
    );
  }
  return (
    <Routes>
      <Route path="/"             element={<Dashboard />} />
      <Route path="/orders"       element={<Orders />} />
      <Route path="/reservations" element={<Reservations />} />
      <Route path="/menu"         element={<Menu />} />
      <Route path="/customers"    element={<Customers />} />
      <Route path="/settings"     element={<Settings />} />
      <Route path="/analytics"    element={<Analytics />} />
      <Route path="/reviews"      element={<Reviews />} />
      <Route path="/campaigns"    element={<Campaigns />} />
      <Route path="/settlements"  element={<Settlements />} />
      <Route path="/loyalty"      element={<Loyalty />} />
      <Route path="/payouts"      element={<Payouts />} />
      <Route path="/team"         element={<Team />} />
      <Route path="*"             element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login"           element={<LoginPage />} />
          <Route path="/register"        element={<RegisterPage />} />
          <Route path="/no-restaurant"   element={<NoRestaurantPage />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password"  element={<ResetPassword />} />
          <Route path="/*" element={
            <ProtectedRoute>
              <Layout><PortalRoutes /></Layout>
            </ProtectedRoute>
          }/>
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
